'use client';

import type { PlanCapability } from '@/types/plan-catalog.generated';
import {
  Briefcase,
  FileCheck,
  FilePen,
  FolderInput,
  HardDrive,
  Inbox,
  type LucideIcon,
  MessagesSquare,
  Scale,
  Sunrise,
  Users,
} from 'lucide-react';

// Single source of truth for the sidebar's sections. Both the expanded
// single-column sidebar (SidebarRail) and the collapsed 56px rail
// (SidebarActionList) read from this list so they can never drift apart.
//
// Group taxonomy mirrors the claude.ai / Linear / Notion convention — a small
// set of tightly-named, audience-agnostic clusters. Each group renders its own
// label header in the expanded rail; the collapsed rail omits the labels and
// just renders the icons. Audience gating (auth-only, practice-only,
// capability-only) still happens per-section, so a user sees a group header
// for every cluster they have at least one section in.
export type SidebarSectionKey =
  | 'chat'
  | 'searchLegislation'
  | 'analyzeDocument'
  | 'generateDocument'
  | 'storage'
  | 'zdielaneSoMnou'
  | 'professionalDirectory'
  | 'inbox'
  | 'mojaKancelaria'
  | 'dnes';

// Rail grouping. The expanded rail draws a small section header above each
// cluster and the collapsed rail renders the icons in the same order (no
// header, just tooltips). The "top" cluster is unlabeled — it carries the
// primary nav (Chats) and the bespoke Customize action, which the rail
// renders inline.
export type SidebarSectionGroup =
  | 'top'
  | 'tools'
  | 'documents'
  | 'professionals'
  | 'practice';

export interface SidebarSection {
  key: SidebarSectionKey;
  icon: LucideIcon;
  // i18n key in the `SidebarHeader` namespace for the rail label.
  labelKey: string;
  // Canonical (locale-stripped) destination. Omitted for not-yet-shipped
  // sections which are rendered disabled.
  href?: string;
  // Extra canonical path prefixes that should also mark this section active
  // (e.g. `/results` belongs to the legislation-search section).
  extraActivePrefixes?: string[];
  group: SidebarSectionGroup;
  // 'locked' is never authored in SIDEBAR_SECTIONS directly — it's the
  // runtime override `visibleSidebarSections` applies to an
  // `upsellWhenMissing` section when the user lacks the required capability
  // (see below). Authored badges stay 'beta' | 'comingSoon'.
  badge?: 'beta' | 'comingSoon' | 'locked';
  disabled?: boolean;
  // When true, the section is only shown to users with practice enabled — i.e.
  // a self-declared *Advokát* (latched `practice_self_enabled`) OR a claimed
  // advocate profile. Gated via `visibleSidebarSections` so the expanded and
  // collapsed rails never drift.
  requiresPractice?: boolean;
  // When set, the section is only shown to users whose plan capabilities map
  // contains this key with a truthy value. Mirrors the `requiresPractice` gate
  // but driven by the plan-catalog capability flags (PlanCapabilities in
  // api/pc.py) rather than the practice toggle. Typed as PlanCapability so
  // typos in future keys are caught at compile time.
  requiresCapabilityFlag?: PlanCapability;
  // Per-section opt-in (#7198): when the user lacks `requiresCapabilityFlag`,
  // most sections simply disappear (the default). A section with this flag
  // set instead STAYS VISIBLE for a signed-in user — `visibleSidebarSections`
  // returns it with `badge` overridden to 'locked' so the rails render it
  // discoverable-but-gated (lock affordance + upsell badge), still linking to
  // its normal `href`. Anonymous visitors are unaffected — they never see a
  // capability-gated section regardless of this flag (no plan to upsell yet).
  // Every other capability-gated section keeps the old hide-on-missing
  // behaviour by simply not setting this.
  upsellWhenMissing?: boolean;
}

// Order here is the order shown in the rail, AND the order of groups (the
// rail groups by reading consecutive sections' group values, then renders a
// header on each group flip — exactly one flip per group boundary).
export const SIDEBAR_SECTIONS: readonly SidebarSection[] = [
  // ── Top, unlabeled cluster (claude.ai primary-nav shape) ───────────────
  {
    key: 'chat',
    icon: MessagesSquare,
    labelKey: 'chats',
    // The Chats nav opens the dedicated chats list page (claude.com pattern);
    // "New chat" is a separate action that goes to '/chat'.
    href: '/chats',
    group: 'top',
  },

  // ── NÁSTROJE (Tools) ────────────────────────────────────────────────────
  {
    key: 'searchLegislation',
    icon: Scale,
    labelKey: 'searchLegislation',
    href: '/search',
    extraActivePrefixes: ['/results'],
    group: 'tools',
  },
  {
    key: 'analyzeDocument',
    icon: FileCheck,
    labelKey: 'analyzeDocument',
    href: '/document-analysis',
    group: 'tools',
  },
  {
    key: 'generateDocument',
    icon: FilePen,
    labelKey: 'generateDocument',
    href: '/generovanie',
    group: 'tools',
    badge: 'beta',
    requiresCapabilityFlag: 'document_generation_enabled',
    // #7198 product decision (2026-09-23): directory-tier advocates without
    // the capability still SEE this entry, locked — the discoverability
    // half of "don't grant the capability, but don't hide the upsell
    // either". /generovanie itself renders the upsell screen for them
    // (DocumentGenerationGate).
    upsellWhenMissing: true,
  },

  // ── DOKUMENTY (Documents) ───────────────────────────────────────────────
  {
    key: 'storage',
    icon: HardDrive,
    labelKey: 'storage',
    href: '/storage',
    group: 'documents',
  },
  {
    // Client's shared-items inbox — "Zdieľané so mnou" (#6325 Slice 3).
    // Visible to all authenticated users (a user can be both an advocate and
    // a client; membership is determined by spis_shares rows, not a profile flag).
    // Auth-only (hidden in anonymous mode) so anonymous visitors don't see it.
    key: 'zdielaneSoMnou',
    icon: FolderInput,
    labelKey: 'zdielaneSoMnou',
    href: '/zdielane',
    group: 'documents',
  },

  // ── ODBORNÍCI (Professionals) ───────────────────────────────────────────
  {
    key: 'professionalDirectory',
    icon: Users,
    labelKey: 'professionalDirectory',
    // Canonical advocate-directory listing (#6323): `/advokati` (sk `/advokati`).
    // The old all-professions `/professional-directory` listing was a duplicate
    // of the same engine and now 301-redirects here (see next.config.mjs).
    href: '/advokati',
    group: 'professionals',
  },
  {
    key: 'inbox',
    icon: Inbox,
    labelKey: 'inbox',
    href: '/inbox',
    group: 'professionals',
    badge: 'beta',
  },

  // ── MOJA KANCELÁRIA (Advocate practice — practice-enabled users only) ──
  // Single row that opens the tabbed /kancelaria page (Spisy default, Klienti
  // tab). The previous peer-nav for /kancelaria/spisy + /kancelaria/klienti
  // was collapsed into one tabbed surface.
  {
    key: 'mojaKancelaria',
    icon: Briefcase,
    labelKey: 'mojaKancelaria',
    href: '/kancelaria',
    group: 'practice',
    requiresPractice: true,
  },
  // The morning brief on its own surface (#9683): a compiled-day view of
  // deadlines/rules for the advocate's cases. Same practice-only audience as
  // mojaKancelaria — every case-bound line needs a practice-enabled account.
  {
    key: 'dnes',
    icon: Sunrise,
    labelKey: 'dnes',
    href: '/today',
    group: 'practice',
    requiresPractice: true,
  },
] as const;

// Sections only an authenticated user can act on; hidden in anonymous mode.
const AUTH_ONLY_SECTIONS: ReadonlySet<SidebarSectionKey> = new Set([
  'inbox',
  'zdielaneSoMnou',
]);

// Whether a section's `requiresCapabilityFlag` is set and the caller's
// capabilities map doesn't carry it truthily. Shared by the filter below and
// the badge-override map so the two can't disagree on what "missing" means.
const isCapabilityMissing = (
  s: SidebarSection,
  capabilities?: Record<string, boolean | number>
): boolean =>
  Boolean(
    s.requiresCapabilityFlag && !capabilities?.[s.requiresCapabilityFlag]
  );

// Single gate applied by BOTH rails (expanded SidebarRail + collapsed
// SidebarActionList) so the two can never drift (#5384): drop auth-only
// sections in anonymous mode, practice-only sections (Cases + Clients) for
// users without practice enabled, and capability-gated sections (e.g.
// generateDocument) for users whose plan lacks the required flag — UNLESS
// the section opts into `upsellWhenMissing` (#7198), in which case a
// signed-in user still gets it back, with `badge` overridden to 'locked' so
// the rails render the discoverable-but-gated affordance instead of
// dropping the row. Anonymous visitors never see a capability-gated section
// either way — there's no plan to upsell them onto yet.
// `sections` defaults to SIDEBAR_SECTIONS; it is a parameter only so tests can
// prove the `upsellWhenMissing` opt-in is decided per section.
export const visibleSidebarSections = (
  opts: {
    isAnonymousMode?: boolean;
    practiceEnabled?: boolean;
    capabilities?: Record<string, boolean | number>;
    /**
     * The caller is signed in but its backend user has not loaded (a
     * transient /users/me failure leaves `capabilities` as `{}`). A missing
     * capability then means "not known", not "not in plan": an
     * `upsellWhenMissing` section stays visible with its OWN badge rather
     * than 'locked' — the same rule `DocumentGenerationGate` applies to the
     * page (#11227). Other capability-gated sections keep hiding.
     */
    capabilitiesUnknown?: boolean;
  },
  sections: readonly SidebarSection[] = SIDEBAR_SECTIONS
): readonly SidebarSection[] =>
  sections
    .filter((s) => {
      if (opts.isAnonymousMode && AUTH_ONLY_SECTIONS.has(s.key)) return false;
      if (s.requiresPractice && !opts.practiceEnabled) return false;
      if (isCapabilityMissing(s, opts.capabilities)) {
        return Boolean(s.upsellWhenMissing) && !opts.isAnonymousMode;
      }
      return true;
    })
    .map(
      (s): SidebarSection =>
        isCapabilityMissing(s, opts.capabilities) && !opts.capabilitiesUnknown
          ? { ...s, badge: 'locked' as const }
          : s
    );

const startsWith = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

// Resolve which section a canonical pathname belongs to, or `null` when the
// route belongs to none of them (e.g. /user-profile, /pricing). Chat is the
// home surface ('/' and '/chat/[id]'); every other section matches its own
// href (+ any extra prefixes).
//
// Returning `null` (rather than defaulting to 'chat') is deliberate: the nav
// must not paint the Chats row active while the user is on an unrelated page.
// AppSidebar separately falls back to the chat history for its recents list,
// so history stays reachable everywhere without the misleading highlight.
export const getActiveSection = (
  pathname: string
): SidebarSectionKey | null => {
  // Chat is the home surface: the landing ('/'), an open session ('/chat/:id'),
  // and the chats list page ('/chats'). startsWith('/chat') does NOT match
  // '/chats' (no shared '/chat/' segment), so '/chats' is matched explicitly.
  if (
    pathname === '/' ||
    startsWith(pathname, '/chat') ||
    startsWith(pathname, '/chats')
  )
    return 'chat';
  for (const section of SIDEBAR_SECTIONS) {
    if (section.key === 'chat') continue;
    const prefixes = [
      ...(section.href ? [section.href] : []),
      ...(section.extraActivePrefixes ?? []),
    ];
    if (prefixes.some((p) => startsWith(pathname, p))) return section.key;
  }
  return null;
};
